'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Users, TrendingUp, ShieldCheck, DollarSign, Search, Download, ChevronUp, ChevronDown, Eye, MoreHorizontal, CheckSquare, BarChart2,  } from 'lucide-react';
import AdminBenefitChart from './AdminBenefitChart';

// ─── Mock enrollment data ─────────────────────────────────────────────────
const MOCK_ENROLLMENTS = [
  {
    id: 'enr-001',
    childName: 'Ayanda Nkosi',
    school: 'Ekurhuleni Primary School',
    grade: 'Grade 5',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'Sanitary Pads', 'On-Site School Safety', 'Life Skills Development Program'],
    enrollDate: '03/01/2026',
    status: 'Active',
    parent: 'Nomsa Nkosi',
    phone: '0712055570',
  },
  {
    id: 'enr-002',
    childName: 'Sipho Dlamini',
    school: 'Tembisa High School',
    grade: 'Grade 10',
    gender: 'Male',
    benefits: ['School Uniform', 'Stationery', 'On-Site School Safety', 'Post-Matric Support', 'Skills Development'],
    enrollDate: '15/01/2026',
    status: 'Active',
    parent: 'Thabo Dlamini',
    phone: '0724740920',
  },
  {
    id: 'enr-003',
    childName: 'Lerato Mokoena',
    school: 'Soweto Primary School',
    grade: 'Grade 7',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'Sanitary Pads', 'Emergency Medical Advance', 'On-Site School Safety', 'Birthday Outings'],
    enrollDate: '20/01/2026',
    status: 'Active',
    parent: 'Dineo Mokoena',
    phone: '0833097332',
  },
  {
    id: 'enr-004',
    childName: 'Bongani Zulu',
    school: 'Alexandra Secondary School',
    grade: 'Grade 11',
    gender: 'Male',
    benefits: ['School Uniform', 'Stationery', 'Data / Library Transport', 'On-Site School Safety', 'Post-Matric Support', 'Life Skills Development Program'],
    enrollDate: '02/02/2026',
    status: 'Active',
    parent: 'Lungelo Zulu',
    phone: '0658947355',
  },
  {
    id: 'enr-005',
    childName: 'Precious Sithole',
    school: 'Meadowlands Primary',
    grade: 'Grade 3',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'On-Site School Safety', 'School Trip Lunch Box'],
    enrollDate: '10/02/2026',
    status: 'Pending',
    parent: 'Beauty Sithole',
    phone: '0712055570',
  },
  {
    id: 'enr-006',
    childName: 'Thandeka Khumalo',
    school: 'Evaton North Secondary',
    grade: 'Grade 9',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'Sanitary Pads', 'Teacher Progress Check', 'On-Site School Safety', 'Life Skills Development Program'],
    enrollDate: '14/02/2026',
    status: 'Active',
    parent: 'Zanele Khumalo',
    phone: '0724740920',
  },
  {
    id: 'enr-007',
    childName: 'Mandla Shabalala',
    school: 'Kagiso Primary School',
    grade: 'Grade 6',
    gender: 'Male',
    benefits: ['School Uniform', 'Stationery', 'On-Site School Safety', 'Birthday Outings', 'Skills Development'],
    enrollDate: '01/03/2026',
    status: 'Active',
    parent: 'Nhlanhla Shabalala',
    phone: '0833097332',
  },
  {
    id: 'enr-008',
    childName: 'Siphokazi Ntuli',
    school: 'Daveyton High School',
    grade: 'Grade 12',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'Sanitary Pads', 'Post-Matric Support', 'On-Site School Safety', 'Death of a Parent/Guardian Benefit'],
    enrollDate: '05/03/2026',
    status: 'Active',
    parent: 'Zodwa Ntuli',
    phone: '0658947355',
  },
  {
    id: 'enr-009',
    childName: 'Luyanda Mthembu',
    school: 'Spruitview Primary',
    grade: 'Grade 4',
    gender: 'Male',
    benefits: ['School Uniform', 'Stationery', 'On-Site School Safety'],
    enrollDate: '12/03/2026',
    status: 'Active',
    parent: 'Sibusiso Mthembu',
    phone: '0712055570',
  },
  {
    id: 'enr-010',
    childName: 'Nokwanda Cele',
    school: 'Umlazi Secondary School',
    grade: 'Grade 8',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'Sanitary Pads', 'Emergency Medical Advance', 'On-Site School Safety'],
    enrollDate: '18/03/2026',
    status: 'Lapsed',
    parent: 'Gcinile Cele',
    phone: '0724740920',
  },
  {
    id: 'enr-011',
    childName: 'Nhlanhla Buthelezi',
    school: 'Vosloorus High School',
    grade: 'Grade 10',
    gender: 'Male',
    benefits: ['School Uniform', 'Stationery', 'Post-Matric Support', 'On-Site School Safety', 'Life Skills Development Program'],
    enrollDate: '25/03/2026',
    status: 'Active',
    parent: 'Mthokozisi Buthelezi',
    phone: '0833097332',
  },
  {
    id: 'enr-012',
    childName: 'Zanele Mahlangu',
    school: 'Benoni Primary School',
    grade: 'Grade 5',
    gender: 'Female',
    benefits: ['School Uniform', 'Stationery', 'Sanitary Pads', 'On-Site School Safety', 'Teacher Progress Check', 'Birthday Outings'],
    enrollDate: '01/04/2026',
    status: 'Pending',
    parent: 'Thandi Mahlangu',
    phone: '0658947355',
  },
];

const STATUS_STYLES: Record<string, string> = {
  Active: 'badge-active',
  Pending: 'badge-pending',
  Lapsed: 'badge-lapsed',
};

const TABS = [
  { id: 'tab-enrollments', label: 'Enrollments', icon: Users },
  { id: 'tab-benefits', label: 'Benefit Selections', icon: CheckSquare },
  { id: 'tab-analytics', label: 'Analytics', icon: BarChart2 },
];

export default function AdminPanelClient() {
  const [activeTab, setActiveTab] = useState('tab-enrollments');
  const [search, setSearch] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortCol, setSortCol] = useState('enrollDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const perPage = 8;

  // BACKEND INTEGRATION: replace MOCK_ENROLLMENTS with API fetch /api/admin/enrollments

  const filtered = useMemo(() => {
    let data = [...MOCK_ENROLLMENTS];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (e) =>
          e.childName.toLowerCase().includes(q) ||
          e.school.toLowerCase().includes(q) ||
          e.parent.toLowerCase().includes(q)
      );
    }
    if (filterGrade) data = data.filter((e) => e.grade === filterGrade);
    if (filterGender) data = data.filter((e) => e.gender === filterGender);
    if (filterStatus) data = data.filter((e) => e.status === filterStatus);

    data.sort((a, b) => {
      let av = a[sortCol as keyof typeof a] as string;
      let bv = b[sortCol as keyof typeof b] as string;
      if (sortDir === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });
    return data;
  }, [search, filterGrade, filterGender, filterStatus, sortCol, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const activeCount = MOCK_ENROLLMENTS.filter((e) => e.status === 'Active').length;
  const pendingCount = MOCK_ENROLLMENTS.filter((e) => e.status === 'Pending').length;
  const monthlyRevenue = activeCount * 250;

  const benefitCounts: Record<string, number> = {};
  MOCK_ENROLLMENTS.forEach((e) => {
    e.benefits.forEach((b) => {
      benefitCounts[b] = (benefitCounts[b] || 0) + 1;
    });
  });

  function toggleSort(col: string) {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  }

  function toggleRow(id: string) {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleAll() {
    if (selectedRows.length === paginated.length) setSelectedRows([]);
    else setSelectedRows(paginated.map((e) => e.id));
  }

  function SortIcon({ col }: { col: string }) {
    if (sortCol !== col) return <ChevronUp size={12} className="opacity-20" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={12} className="text-primary" />
    ) : (
      <ChevronDown size={12} className="text-primary" />
    );
  }

  const uniqueGrades = Array.from(new Set(MOCK_ENROLLMENTS.map((e) => e.grade))).sort();

  return (
    <div className="max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Panel</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Luvare Collective Pty Ltd — Enrollment Management
          </p>
        </div>
        <button
          onClick={() => toast.success('Export started — CSV downloading...')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-secondary active:scale-95 transition-all duration-150"
        >
          <Download size={15} />
          Export CSV
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Total Enrolled
            </span>
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users size={16} className="text-primary" />
            </div>
          </div>
          <p className="text-3xl font-bold text-foreground tabular-nums">
            {MOCK_ENROLLMENTS.length}
          </p>
          <p className="text-xs text-muted-foreground mt-1">children registered</p>
        </div>

        <div className="bg-card border border-emerald-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Active Members
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ShieldCheck size={16} className="text-emerald-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-emerald-700 tabular-nums">{activeCount}</p>
          <p className="text-xs text-muted-foreground mt-1">{pendingCount} pending activation</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Monthly Revenue
            </span>
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <DollarSign size={16} className="text-accent" />
            </div>
          </div>
          <p className="text-3xl font-bold text-foreground tabular-nums">
            R{monthlyRevenue.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground mt-1">active × R250/month</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Top Benefit
            </span>
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp size={16} className="text-primary" />
            </div>
          </div>
          <p className="text-base font-bold text-foreground leading-snug">On-Site School Safety</p>
          <p className="text-xs text-muted-foreground mt-1">
            {benefitCounts['On-Site School Safety'] || 0} selections
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-secondary p-1 rounded-xl mb-6 w-fit">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                isActive
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── ENROLLMENTS TAB ─────────────────────────────────── */}
      {activeTab === 'tab-enrollments' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          {/* Filters */}
          <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search child, school, parent..."
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <select
              value={filterGrade}
              onChange={(e) => { setFilterGrade(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Grades</option>
              {uniqueGrades.map((g) => (
                <option key={`filter-grade-${g}`} value={g}>{g}</option>
              ))}
            </select>
            <select
              value={filterGender}
              onChange={(e) => { setFilterGender(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Genders</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Lapsed">Lapsed</option>
            </select>
            <span className="text-xs text-muted-foreground ml-auto tabular-nums">
              {filtered.length} results
            </span>
          </div>

          {/* Bulk action bar */}
          {selectedRows.length > 0 && (
            <div className="px-5 py-3 bg-primary/5 border-b border-primary/20 flex items-center gap-3">
              <span className="text-sm font-semibold text-primary">
                {selectedRows.length} selected
              </span>
              <button
                onClick={() => {
                  toast.success(`${selectedRows.length} records exported`);
                  setSelectedRows([]);
                }}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                Export Selected
              </button>
              <button
                onClick={() => setSelectedRows([])}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-secondary">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedRows.length === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      className="w-4 h-4 accent-primary"
                    />
                  </th>
                  {[
                    { key: 'childName', label: 'Child Name' },
                    { key: 'school', label: 'School' },
                    { key: 'grade', label: 'Grade' },
                    { key: 'gender', label: 'Gender' },
                    { key: 'status', label: 'Status' },
                    { key: 'enrollDate', label: 'Enroll Date' },
                  ].map((col) => (
                    <th
                      key={`th-${col.key}`}
                      onClick={() => toggleSort(col.key)}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        <SortIcon col={col.key} />
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((enr, idx) => (
                  <tr
                    key={enr.id}
                    className={`border-t border-border hover:bg-secondary/50 transition-colors ${
                      selectedRows.includes(enr.id) ? 'bg-primary/5' : idx % 2 === 0 ? 'bg-card' : 'bg-secondary/20'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(enr.id)}
                        onChange={() => toggleRow(enr.id)}
                        className="w-4 h-4 accent-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{enr.childName}</p>
                        <p className="text-xs text-muted-foreground">{enr.parent}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground truncate max-w-[160px]">{enr.school}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground tabular-nums">{enr.grade}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{enr.gender}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[enr.status]}`}>
                        {enr.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground tabular-nums">{enr.enrollDate}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toast.success(`Viewing ${enr.childName}'s enrollment`)}
                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="View enrollment details"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => toast.success(`More options for ${enr.childName}`)}
                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="More options"
                        >
                          <MoreHorizontal size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground tabular-nums">
              Showing {((page - 1) * perPage) + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={`page-${p}`}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                    page === p
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── BENEFIT SELECTIONS TAB ──────────────────────────── */}
      {activeTab === 'tab-benefits' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-base font-bold text-foreground">Benefit Selections per Child</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              All selected benefits for each enrolled child
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-secondary">
                  {['Child Name', 'School', 'Grade', 'Gender', 'Selected Benefits', 'Enroll Date'].map((h) => (
                    <th
                      key={`bth-${h}`}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_ENROLLMENTS.map((enr, idx) => (
                  <tr
                    key={enr.id}
                    className={`border-t border-border hover:bg-secondary/50 transition-colors ${
                      idx % 2 === 0 ? 'bg-card' : 'bg-secondary/20'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-foreground">{enr.childName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground truncate max-w-[140px]">{enr.school}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{enr.grade}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          enr.gender === 'Female' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {enr.gender}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5 max-w-[320px]">
                        {enr.benefits.map((b) => (
                          <span
                            key={`${enr.id}-benefit-${b}`}
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground tabular-nums">{enr.enrollDate}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── ANALYTICS TAB ───────────────────────────────────── */}
      {activeTab === 'tab-analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-base font-bold mb-1">Benefit Selection Frequency</h3>
            <p className="text-xs text-muted-foreground mb-4">
              How many children selected each benefit
            </p>
            <AdminBenefitChart data={benefitCounts} />
          </div>

          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-base font-bold mb-4">Grade Distribution</h3>
            <div className="flex flex-col gap-3">
              {uniqueGrades.map((g) => {
                const count = MOCK_ENROLLMENTS.filter((e) => e.grade === g).length;
                const pct = Math.round((count / MOCK_ENROLLMENTS.length) * 100);
                return (
                  <div key={`grade-dist-${g}`} className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-muted-foreground w-20 flex-shrink-0">
                      {g}
                    </span>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold tabular-nums text-foreground w-6 text-right">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-5 border-t border-border">
              <h4 className="text-sm font-bold mb-3">Gender Split</h4>
              <div className="flex items-center gap-6">
                {[
                  { label: 'Female', color: 'bg-pink-400', gender: 'Female' },
                  { label: 'Male', color: 'bg-blue-400', gender: 'Male' },
                ].map((g) => {
                  const count = MOCK_ENROLLMENTS.filter((e) => e.gender === g.gender).length;
                  const pct = Math.round((count / MOCK_ENROLLMENTS.length) * 100);
                  return (
                    <div key={`gender-split-${g.gender}`} className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${g.color}`} />
                      <span className="text-sm text-foreground font-semibold">
                        {g.label}: {count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}